import './GoogleMapsApiBoundary.scss';

import { captureException } from '@sentry/react';
import { APILoadingStatus, APIProvider, useApiLoadingStatus } from '@vis.gl/react-google-maps';
import Alert from 'components/Alert/Alert';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { googleMapsApiOptions } from 'helpers/googleMapsConfig';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { PropsWithChildren } from 'react';

const reportedLoadErrors = new Set<unknown>();

const reportLoadError = (error: unknown) => {
  // Several visible maps can observe the same rejected API-loading promise.
  if (reportedLoadErrors.has(error)) return;
  reportedLoadErrors.add(error);
  captureException(error, {
    tags: { feature: 'maps', maps_stage: 'api_load', maps_loader: 'vis.gl' }
  });
};

const GoogleMapsApiContent = ({ children }: PropsWithChildren) => {
  const status = useApiLoadingStatus();
  const { tError } = useCustomTranslation();

  if (status === APILoadingStatus.FAILED || status === APILoadingStatus.AUTH_FAILURE) {
    return (
      <div className="GoogleMapsApiBoundary_status" role="alert">
        <Alert type="error" message={tError('map_load_failed')} />
      </div>
    );
  }

  if (status !== APILoadingStatus.LOADED) {
    return (
      <div className="GoogleMapsApiBoundary_status" aria-busy="true">
        <LoadingIndicator />
      </div>
    );
  }

  return children;
};

/**
 * Mount only with a map, so other pages do not load the Maps API.
 * Providers use identical options and vis.gl shares one API loader across them.
 */
const GoogleMapsApiBoundary = ({ children }: PropsWithChildren) => (
  <APIProvider {...googleMapsApiOptions} onError={reportLoadError}>
    <GoogleMapsApiContent>{children}</GoogleMapsApiContent>
  </APIProvider>
);

export default GoogleMapsApiBoundary;
